import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';
import PageHeader from './PageHeader';

export default function DuplicateChecker({ isDarkMode }) {
  const navigate = useNavigate();
  const [duplicates, setDuplicates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDuplicate, setSelectedDuplicate] = useState(null);
  const [loadProgress, setLoadProgress] = useState({ current: 0, total: 0, companies: 0 });
  const [allCompanies, setAllCompanies] = useState([]);
  const [isLoadingAll, setIsLoadingAll] = useState(false);
  const [fixingIcos, setFixingIcos] = useState(false);
  const [fixedCount, setFixedCount] = useState(0);
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showUserFilter, setShowUserFilter] = useState(false);
  const [selectedForDeletion, setSelectedForDeletion] = useState(new Set());
  const [showPriorityModal, setShowPriorityModal] = useState(false);
  const [preferredUsers, setPreferredUsers] = useState([]);
  const [tempPreferredUsers, setTempPreferredUsers] = useState([]);
  const [expandedActivities, setExpandedActivities] = useState({}); // { [companyId]: activities[] | 'loading' | 'error' }

  // Normalizuje IČO na 8 číslic (doplní nuly zleva)
  const normalizeIco = (ico) => {
    if (!ico) return '';
    const trimmed = ico.toString().trim();
    // Odstraň nečíselné znaky
    const numbersOnly = trimmed.replace(/\D/g, '');
    // Doplň nuly zleva do 8 číslic
    return numbersOnly.padStart(8, '0');
  };

  // Opraví všechna IČO v databázi (doplní nuly)
  const fixAllIcos = async () => {
    if (!window.confirm('Opravdu chcete normalizovat všechna IČO v celém CRM? Tato operace může trvat několik minut.')) {
      return;
    }

    setFixingIcos(true);
    setFixedCount(0);
    setLoadProgress({ current: 0, total: 0, companies: 0 });

    try {
      // Načti všechny firmy
      const usersRes = await fetch(`${API_URL}/users?userId=${localStorage.getItem('lastUserId') || ''}`);
      if (!usersRes.ok) {
        alert('Chyba při načítání uživatelů');
        setFixingIcos(false);
        return;
      }

      const users = await usersRes.json();
      setLoadProgress({ current: 0, total: users.length, companies: 0 });

      let totalFixed = 0;

      // Projdi všechny uživatele a jejich firmy
      for (let i = 0; i < users.length; i++) {
        const user = users[i];
        setLoadProgress({ current: i + 1, total: users.length, companies: 0 });

        try {
          // Načti všechny firmy po stránkách
          let currentPage = 1;
          let hasMore = true;
          
          while (hasMore) {
            const companiesRes = await fetch(`${API_URL}/companies?userId=${user._id}&page=${currentPage}&limit=500`);
            
            if (companiesRes.ok) {
              const response = await companiesRes.json();
              let companies;
              
              if (response.companies) {
                companies = response.companies;
                hasMore = response.pagination?.hasMore || response.pagination?.hasNextPage || companies.length === 500;
              } else if (Array.isArray(response)) {
                companies = response;
                hasMore = companies.length === 500;
              } else {
                companies = [];
                hasMore = false;
              }
              
              if (companies.length === 0) {
                hasMore = false;
                break;
              }

              // Projdi všechny firmy na této stránce
              for (const company of companies) {
                if (company.ico) {
                  const originalIco = company.ico.toString();
                  const normalizedIco = normalizeIco(originalIco);

                  // Pokud se IČO změnilo, ulož ho
                  if (originalIco !== normalizedIco) {
                    try {
                      const updateRes = await fetch(`${API_URL}/companies/${company._id}`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          ...company,
                          ico: normalizedIco
                        })
                      });

                      if (updateRes.ok) {
                        totalFixed++;
                        setFixedCount(totalFixed);
                      }
                    } catch (err) {
                      console.error(`Chyba při aktualizaci IČO pro firmu ${company._id}:`, err);
                    }
                  }
                }
              }
              
              currentPage++;
            } else {
              hasMore = false;
            }
          }
        } catch (err) {
          console.error(`Chyba při načítání firem pro uživatele ${user.username}:`, err);
        }
      }

      alert(`Hotovo! Normalizováno ${totalFixed} IČO.`);
      // Po opravě načti znovu všechny firmy
      await loadAllCompanies();
    } catch (error) {
      console.error('Chyba při normalizaci IČO:', error);
      alert('Chyba při normalizaci IČO: ' + error.message);
    } finally {
      setFixingIcos(false);
    }
  };

  // Načíst VŠECHNY firmy z CRM postupně
  const loadAllCompanies = async () => {
    setIsLoadingAll(true);
    setLoadProgress({ current: 0, total: 0, companies: 0 });
    
    try {
      // Nejprve získej seznam všech uživatelů
      const usersRes = await fetch(`${API_URL}/users?userId=${localStorage.getItem('lastUserId') || ''}`);
      if (!usersRes.ok) {
        console.error('Chyba při načítání uživatelů');
        alert('Chyba při načítání uživatelů');
        setIsLoadingAll(false);
        return;
      }
      
      const usersData = await usersRes.json();
      setUsers(usersData); // Ulož uživatele pro filtrování
      setLoadProgress({ current: 0, total: usersData.length, companies: 0 });
      
      // Načti firmy pro každého uživatele postupně
      const allCompaniesData = [];
      
      for (let i = 0; i < usersData.length; i++) {
        const user = usersData[i];
        
        try {
          // PRO VŠECHNY uživatele používej postupné načítání po stránkách
          let userCompanies = [];
          let currentPage = 1;
          let hasMore = true;
          
          while (hasMore) {
            const apiUrl = `${API_URL}/companies?userId=${user._id}&page=${currentPage}&limit=500`;
            const companiesRes = await fetch(apiUrl);
            
            if (companiesRes.ok) {
              const response = await companiesRes.json();
              let pageCompanies;
              
              if (response.companies) {
                pageCompanies = response.companies;
                // Použij pagination info z backendu
                if (response.pagination?.hasMore !== undefined) {
                  hasMore = response.pagination.hasMore;
                } else if (response.pagination?.hasNextPage !== undefined) {
                  hasMore = response.pagination.hasNextPage;
                } else {
                  // Fallback - pokud dostaneme méně než limit, je to konec
                  hasMore = pageCompanies.length === 500;
                }
              } else if (Array.isArray(response)) {
                pageCompanies = response;
                hasMore = response.length === 500;
              } else {
                pageCompanies = [];
                hasMore = false;
              }
              
              // Pokud je stránka prázdná, konec
              if (pageCompanies.length === 0) {
                hasMore = false;
                break;
              }
              
              userCompanies.push(...pageCompanies);
              allCompaniesData.push(...pageCompanies);
              
              // Aktualizuj progress s počtem firem
              setLoadProgress({ 
                current: i + 1, 
                total: usersData.length, 
                companies: allCompaniesData.length 
              });
              
              console.log(`✅ ${user.username} - Stránka ${currentPage}: ${pageCompanies.length} firem (uživatel: ${userCompanies.length}, celkem: ${allCompaniesData.length})`);
              
              currentPage++;
              
              // Bezpečnostní limit
              if (currentPage > 10000) {
                console.warn('⚠️ Dosažen bezpečnostní limit 10000 stránek');
                hasMore = false;
              }
            } else {
              console.error(`Chyba při načítání stránky ${currentPage} pro ${user.username}`);
              hasMore = false;
            }
          }
          
          console.log(`📊 ${user.username}: Celkem načteno ${userCompanies.length} firem`);
        } catch (err) {
          console.error(`Chyba při načítání firem pro uživatele ${user.username}:`, err);
        }
      }
      
      console.log(`🎉 CELKEM načteno ${allCompaniesData.length} firem ze všech uživatelů`);
      setAllCompanies(allCompaniesData);
      findDuplicates(allCompaniesData);
    } catch (error) {
      console.error('Chyba při načítání všech firem:', error);
      alert('Chyba při načítání všech firem: ' + error.message);
    } finally {
      setIsLoadingAll(false);
    }
  };

  // Najde všechny duplicity podle IČO
  const findDuplicates = (contactsToCheck = allCompanies) => {
    setLoading(true);
    
    // Filtruj podle vybraných uživatelů (pokud jsou nějaké vybraní)
    let filteredContacts = contactsToCheck;
    if (selectedUsers.length > 0) {
      filteredContacts = contactsToCheck.filter(contact => {
        const ownerId = contact.createdBy?._id || contact.createdBy;
        return selectedUsers.includes(ownerId);
      });
    }
    
    // Seskup kontakty podle normalizovaného IČO (ignoruj prázdná IČO)
    const icoGroups = {};
    
    filteredContacts.forEach(contact => {
      const ico = normalizeIco(contact.ico);
      if (ico && ico !== '00000000') { // Ignoruj prázdná nebo nulová IČO
        if (!icoGroups[ico]) {
          icoGroups[ico] = [];
        }
        icoGroups[ico].push(contact);
      }
    });
    
    // Najde pouze skupiny s více než jedním kontaktem (duplicity)
    const duplicateGroups = Object.entries(icoGroups)
      .filter(([ico, contacts]) => contacts.length > 1)
      .map(([ico, contacts]) => ({
        ico,
        contacts,
        count: contacts.length
      }))
      .sort((a, b) => b.count - a.count); // Seřaď podle počtu duplicit
    
    setDuplicates(duplicateGroups);
    setLoading(false);
  };

  // Smazat firmu
  const deleteCompany = async (companyId) => {
    if (!window.confirm('Opravdu chcete smazat tuto firmu? Tato akce je nevratná!')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/companies/${companyId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        // Odstraň firmu z allCompanies
        const updatedCompanies = allCompanies.filter(c => c._id !== companyId);
        setAllCompanies(updatedCompanies);
        
        // Přepočítej duplicity
        findDuplicates(updatedCompanies);
        
        // Pokud byla vybraná duplicita, aktualizuj ji
        if (selectedDuplicate) {
          const updatedContacts = selectedDuplicate.contacts.filter(c => c._id !== companyId);
          if (updatedContacts.length > 1) {
            setSelectedDuplicate({
              ...selectedDuplicate,
              contacts: updatedContacts,
              count: updatedContacts.length
            });
          } else {
            setSelectedDuplicate(null);
          }
        }
        
        alert('Firma byla úspěšně smazána');
      } else {
        alert('Chyba při mazání firmy');
      }
    } catch (error) {
      console.error('Chyba při mazání firmy:', error);
      alert('Chyba při mazání firmy: ' + error.message);
    }
  };

  // Toggle výběr uživatele
  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  // Aplikovat filtr uživatelů
  const applyUserFilter = () => {
    findDuplicates(allCompanies);
    setShowUserFilter(false);
  };

  // Načti automaticky všechny firmy při otevření
  useEffect(() => {
    loadAllCompanies();
  }, []);

  // Inicializace tempPreferredUsers při otevření modalu
  useEffect(() => {
    if (showPriorityModal) {
      setTempPreferredUsers(preferredUsers);
    }
  }, [showPriorityModal]);

  // Zobraz detail duplicity
  const showDuplicateDetails = (duplicateGroup) => {
    setSelectedDuplicate(duplicateGroup);
  };

  // Přejdi na detail firmy v novém okně
  const openCompanyDetail = (company) => {
    window.open(`/#/companies/${company._id}`, '_blank');
  };

  // Toggle výběr firmy pro smazání
  const toggleCompanyForDeletion = (companyId) => {
    setSelectedForDeletion(prev => {
      const newSet = new Set(prev);
      if (newSet.has(companyId)) {
        newSet.delete(companyId);
      } else {
        newSet.add(companyId);
      }
      return newSet;
    });
  };

  // Automatické označení duplicit podle priorit
  const autoMarkByPriority = () => {
    if (duplicates.length === 0) {
      alert('Nejsou k dispozici žádné duplicity');
      return;
    }

    const newSelectedForDeletion = new Set();
    let totalMarked = 0;

    // Pro každou skupinu duplicit
    duplicates.forEach(duplicateGroup => {
      const companies = duplicateGroup.companies;
      
      if (companies.length < 2) return; // Není co označit

      // Priorita 1: Preferovaní obchodníci (pokud jsou nastavení)
      if (preferredUsers.length > 0) {
        const preferredCompanies = companies.filter(c => 
          preferredUsers.includes(c.createdBy?._id || c.createdBy)
        );
        const nonPreferredCompanies = companies.filter(c => 
          !preferredUsers.includes(c.createdBy?._id || c.createdBy)
        );

        // Pokud existují preferovaní, označ nepreferované
        if (preferredCompanies.length > 0 && nonPreferredCompanies.length > 0) {
          nonPreferredCompanies.forEach(c => {
            newSelectedForDeletion.add(c._id);
            totalMarked++;
          });
          return; // Hotovo pro tuto skupinu
        }
      }

      // Priorita 2: Role - Obchodníci > Kontakty > Databáze
      // Najdi nejvyšší prioritu v této skupině
      const getRolePriority = (company) => {
        const user = users.find(u => u._id === (company.createdBy?._id || company.createdBy));
        if (!user) return 0;
        
        const role = user.role;
        if (role === 'admin' || role === 'teamleader' || role === 'salesman') return 3; // Obchodníci
        if (role === 'contacts') return 2; // Kontakty
        if (role === 'database') return 1; // Databáze
        return 0;
      };

      const maxPriority = Math.max(...companies.map(getRolePriority));
      
      // Označ všechny s nižší prioritou
      companies.forEach(company => {
        const priority = getRolePriority(company);
        if (priority < maxPriority) {
          newSelectedForDeletion.add(company._id);
          totalMarked++;
        }
      });
    });

    setSelectedForDeletion(newSelectedForDeletion);
    alert(`Automaticky označeno ${totalMarked} firem ke smazání podle priorit.\n\nZkontrolujte výběr před potvrzením smazání!`);
  };

  // Hromadné smazání vybraných firem
  const deleteSelectedCompanies = async () => {
    if (selectedForDeletion.size === 0) {
      alert('Nevybrali jste žádné firmy ke smazání');
      return;
    }

    if (!window.confirm(`Opravdu chcete smazat ${selectedForDeletion.size} vybraných firem? Tato akce je nevratná!`)) {
      return;
    }

    const companyIds = Array.from(selectedForDeletion);
    let deletedCount = 0;
    let errorCount = 0;

    for (const companyId of companyIds) {
      try {
        const response = await fetch(`${API_URL}/companies/${companyId}`, {
          method: 'DELETE'
        });

        if (response.ok) {
          deletedCount++;
        } else {
          errorCount++;
        }
      } catch (error) {
        console.error('Chyba při mazání firmy:', companyId, error);
        errorCount++;
      }
    }

    // Odstraň smazané firmy z allCompanies
    const updatedCompanies = allCompanies.filter(c => !selectedForDeletion.has(c._id));
    setAllCompanies(updatedCompanies);
    
    // Přepočítej duplicity
    findDuplicates(updatedCompanies);
    
    // Vyčisti výběr
    setSelectedForDeletion(new Set());
    setSelectedDuplicate(null);
    
    alert(`Hotovo!\nSmazáno: ${deletedCount} firem\nChyby: ${errorCount}`);
  };

  // Získej jméno vlastníka
  const getOwnerName = (contact) => {
    if (typeof contact.createdBy === 'object' && contact.createdBy?.name) {
      return contact.createdBy.name;
    }
    if (typeof contact.createdBy === 'string') {
      return contact.createdBy;
    }
    return 'Neznámý';
  };

  const toggleActivities = async (companyId) => {
    // Pokud už jsou načteny nebo načítáme, jen skryć/zobraz
    if (expandedActivities[companyId] !== undefined) {
      setExpandedActivities(prev => {
        const next = { ...prev };
        delete next[companyId];
        return next;
      });
      return;
    }
    // Načti aktivity
    setExpandedActivities(prev => ({ ...prev, [companyId]: 'loading' }));
    try {
      const res = await fetch(`${API_URL}/companies/${companyId}`);
      if (!res.ok) throw new Error('Chyba');
      const data = await res.json();
      const activities = data.activities || [];
      setExpandedActivities(prev => ({ ...prev, [companyId]: activities }));
    } catch (e) {
      setExpandedActivities(prev => ({ ...prev, [companyId]: 'error' }));
    }
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      
      {/* Page Header */}
      <PageHeader
        title="🔍 Ověření duplicitních IČO"
        isDarkMode={isDarkMode}
        actions={
          <button
            onClick={() => navigate('/contact-management')}
            className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
              isDarkMode
                ? 'bg-gray-700 hover:bg-gray-600 text-white'
                : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
            }`}
          >
            ← Zpět na správu kontaktů
          </button>
        }
      />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-6">
        
        {/* Status a progress */}
        <div className={`mb-6 p-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {fixingIcos
              ? `Normalizuji IČO... (${loadProgress.current}/${loadProgress.total} uživatelů, opraveno ${fixedCount})`
              : isLoadingAll 
                ? `Načítám všechny firmy z celého CRM... (${loadProgress.current}/${loadProgress.total} uživatelů, ${(loadProgress.companies ?? 0).toLocaleString('cs-CZ')} firem)`
                : allCompanies.length > 0
                  ? `Nalezeno ${duplicates.length} skupin duplicitních IČO z celkem ${allCompanies.length.toLocaleString('cs-CZ')} firem${selectedUsers.length > 0 ? ` (filtrováno ${selectedUsers.length} uživatelů)` : ' (celé CRM)'}`
                  : 'Načítám data...'
            }
          </p>
        </div>

        {/* Action buttons */}
        <div className={`mb-6 p-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <div className="flex gap-2 flex-wrap">
              {!isLoadingAll && !fixingIcos && (
                <>
                  {selectedForDeletion.size > 0 && (
                    <button
                      onClick={deleteSelectedCompanies}
                      className={`px-4 py-2 rounded font-semibold transition ${
                        isDarkMode 
                          ? 'bg-red-700 hover:bg-red-800 text-white' 
                          : 'bg-red-600 hover:bg-red-700 text-white'
                      }`}
                      title="Smazat vybrané firmy"
                    >
                      🗑️ Smazat vybrané ({selectedForDeletion.size})
                    </button>
                  )}
                  <button
                    onClick={() => setShowPriorityModal(true)}
                    className={`px-4 py-2 rounded font-semibold transition ${
                      preferredUsers.length > 0
                        ? (isDarkMode ? 'bg-purple-700 hover:bg-purple-800 text-white' : 'bg-purple-600 hover:bg-purple-700 text-white')
                        : (isDarkMode ? 'bg-purple-600 hover:bg-purple-700 text-white' : 'bg-purple-500 hover:bg-purple-600 text-white')
                    }`}
                    title="Nastavit priority pro automatické označení duplicit"
                  >
                    ⚡ Označit podle priorit {preferredUsers.length > 0 && `(${preferredUsers.length})`}
                  </button>
                  <button
                    onClick={() => setShowUserFilter(true)}
                    className={`px-4 py-2 rounded font-semibold transition ${
                      selectedUsers.length > 0
                        ? (isDarkMode ? 'bg-blue-700 hover:bg-blue-800 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white')
                        : (isDarkMode ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white')
                    }`}
                    title="Filtrovat podle uživatelů"
                  >
                    👥 Filtr uživatelů {selectedUsers.length > 0 && `(${selectedUsers.length})`}
                  </button>
                  <button
                    onClick={fixAllIcos}
                    className={`px-4 py-2 rounded font-semibold transition ${
                      isDarkMode 
                        ? 'bg-green-600 hover:bg-green-700 text-white' 
                        : 'bg-green-500 hover:bg-green-600 text-white'
                    }`}
                    title="Doplní nuly do všech IČO v CRM aby měla 8 číslic"
                  >
                    🔧 Normalizovat IČO
                  </button>
                </>
              )}
          </div>
        </div>

        {/* Content */}
        <div className={`rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <div className="flex" style={{height: 'calc(100vh - 280px)'}}>
          {/* Left panel - Seznam duplicit */}
          <div className={`w-1/2 border-r overflow-y-auto ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="p-4">
              <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Seznam duplicitních IČO
              </h3>
              
              {(loading || isLoadingAll || fixingIcos) ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <p className={`mt-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {fixingIcos 
                      ? `Normalizuji IČO... (opraveno ${fixedCount})`
                      : isLoadingAll 
                        ? 'Načítám firmy ze všech účtů...' 
                        : 'Hledám duplicity...'
                    }
                  </p>
                </div>
              ) : duplicates.length === 0 ? (
                <div className={`text-center py-8 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  <div className="text-4xl mb-2">🎉</div>
                  <p>Žádné duplicitní IČO nenalezeno v celém CRM!</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {duplicates.map((duplicateGroup, index) => {
                    // Spočítej kolik firem z této skupiny je označeno ke smazání
                    const markedCount = (duplicateGroup.contacts || []).filter(c => selectedForDeletion.has(c._id)).length;
                    
                    return (
                    <div
                      key={duplicateGroup.ico}
                      className={`p-3 rounded border cursor-pointer transition ${
                        selectedDuplicate?.ico === duplicateGroup.ico
                          ? (isDarkMode ? 'bg-blue-900 border-blue-600' : 'bg-blue-50 border-blue-300')
                          : (isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-50 border-gray-200 hover:bg-gray-100')
                      }`}
                      onClick={() => showDuplicateDetails(duplicateGroup)}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            IČO: {duplicateGroup.ico}
                          </p>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            {duplicateGroup.count} duplicitních záznamů
                            {markedCount > 0 && (
                              <span className={`ml-2 px-2 py-0.5 rounded text-xs font-bold ${
                                isDarkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-800'
                              }`}>
                                {markedCount} označeno
                              </span>
                            )}
                          </p>
                        </div>
                        <div className={`px-2 py-1 rounded text-sm font-bold ${
                          duplicateGroup.count > 3 
                            ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            : 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                        }`}>
                          {duplicateGroup.count}×
                        </div>
                      </div>
                      {/* Zobraz vlastníky */}
                      <div className={`text-xs mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {duplicateGroup.contacts.map((c, i) => (
                          <div key={i} className="truncate">
                            <span className="font-medium">Vlastník {String.fromCharCode(65 + i)}:</span> {getOwnerName(c)}
                          </div>
                        ))}
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Right panel - Detail duplicity */}
          <div className="w-1/2 overflow-y-auto">
            <div className="p-4">
              {selectedDuplicate ? (
                <div>
                  <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Detail duplicity IČO: {selectedDuplicate.ico}
                  </h3>
                  
                  <div className="space-y-4">
                    {selectedDuplicate.contacts.map((contact, index) => (
                      <div
                        key={contact._id}
                        className={`p-4 rounded border ${
                          selectedForDeletion.has(contact._id)
                            ? (isDarkMode ? 'bg-red-900 border-red-600' : 'bg-red-50 border-red-300')
                            : (isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200')
                        } shadow`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-start gap-3 flex-1">
                            <input
                              type="checkbox"
                              checked={selectedForDeletion.has(contact._id)}
                              onChange={() => toggleCompanyForDeletion(contact._id)}
                              className="w-5 h-5 mt-1"
                              title="Vybrat ke smazání"
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`px-2 py-1 rounded text-xs font-bold ${
                                  isDarkMode ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-800'
                                }`}>
                                  Vlastník {String.fromCharCode(65 + index)}
                                </span>
                              </div>
                              <h4 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {contact.name || 'Bez názvu'}
                              </h4>
                              <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                IČO: {contact.ico}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => deleteCompany(contact._id)}
                              className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-sm rounded transition"
                              title="Smazat tuto firmu"
                            >
                              🗑️ Smazat
                            </button>
                            <button
                              onClick={() => openCompanyDetail(contact)}
                              className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded transition"
                              title="Otevřít detail firmy v novém okně"
                            >
                              👁️ Zobrazit
                            </button>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 gap-2 text-sm">
                          <div>
                            <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              Vlastník účtu: 
                            </span>
                            <span className={`ml-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {getOwnerName(contact)}
                            </span>
                          </div>
                          
                          {contact.status && (
                            <div>
                              <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                Stav: 
                              </span>
                              <span className={`ml-1 px-2 py-1 rounded text-xs ${
                                contact.status === 'Hotovo' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
                                contact.status === 'Nemá zájem' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300' :
                                'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                              }`}>
                                {contact.status}
                              </span>
                            </div>
                          )}
                          
                          {contact.address && (
                            <div>
                              <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                Adresa: 
                              </span>
                              <span className={`ml-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {contact.address}
                              </span>
                            </div>
                          )}
                          
                          {contact.contacts?.[0]?.phone && (
                            <div>
                              <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                Telefon: 
                              </span>
                              <span className={`ml-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {contact.contacts[0].phone}
                              </span>
                            </div>
                          )}
                          
                          <div className="mt-2">
                            <button
                              onClick={() => toggleActivities(contact._id)}
                              className={`text-sm px-3 py-1.5 rounded font-medium transition ${
                                expandedActivities[contact._id] !== undefined
                                  ? (isDarkMode ? 'bg-blue-700 hover:bg-blue-800 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white')
                                  : (isDarkMode ? 'bg-gray-600 hover:bg-gray-500 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700')
                              }`}
                            >
                              {expandedActivities[contact._id] !== undefined ? '▲ Skrýt aktivity' : '▼ Zobrazit aktivity'}
                            </button>

                            {expandedActivities[contact._id] !== undefined && (
                              <div className={`mt-2 rounded-lg p-3 text-sm ${
                                isDarkMode ? 'bg-gray-900' : 'bg-gray-50'
                              }`}>
                                {expandedActivities[contact._id] === 'loading' ? (
                                  <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Načítám aktivity...</p>
                                ) : expandedActivities[contact._id] === 'error' ? (
                                  <p className="text-red-500">Chyba při načítání aktivit.</p>
                                ) : expandedActivities[contact._id].length === 0 ? (
                                  <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>U této firmy nejsou žádné aktivity.</p>
                                ) : (
                                  <ul className="space-y-2 max-h-48 overflow-y-auto">
                                    {expandedActivities[contact._id].map((act, i) => (
                                      <li key={act._id || i} className={`p-2 rounded border ${
                                        isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                                      }`}>
                                        <div className={`font-medium text-xs leading-snug ${
                                          isDarkMode ? 'text-white' : 'text-gray-900'
                                        }`}>
                                          {act.text || '(bez textu)'}
                                        </div>
                                        <div className={`text-xs mt-1 ${
                                          isDarkMode ? 'text-gray-400' : 'text-gray-500'
                                        }`}>
                                          {act.createdAt ? new Date(act.createdAt).toLocaleString('cs-CZ') : ''}
                                          {act.status && <span className="ml-2">🏷️ {act.status}</span>}
                                        </div>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <div className="text-4xl mb-4">👈</div>
                  <p>Vyberte skupinu duplicit ze seznamu vlevo</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal pro filtr uživatelů */}
      {showUserFilter && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className={`max-w-2xl w-full max-h-[80vh] overflow-hidden rounded-lg shadow-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`px-6 py-4 border-b ${isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-50'}`}>
              <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                👥 Filtrovat podle uživatelů
              </h3>
              <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Vyberte uživatele, jejichž firmy chcete kontrolovat
              </p>
            </div>

            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="space-y-2">
                {users.map(user => (
                  <label
                    key={user._id}
                    className={`flex items-center gap-3 p-3 rounded cursor-pointer transition ${
                      selectedUsers.includes(user._id)
                        ? (isDarkMode ? 'bg-blue-900 border-blue-600' : 'bg-blue-50 border-blue-300')
                        : (isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-50 hover:bg-gray-100')
                    } border`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user._id)}
                      onChange={() => toggleUserSelection(user._id)}
                      className="w-5 h-5"
                    />
                    <div className="flex-1">
                      <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {user.username || user.name || 'Bez jména'}
                      </div>
                      {user.email && (
                        <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {user.email}
                        </div>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className={`px-6 py-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} flex justify-between`}>
              <button
                onClick={() => {
                  setSelectedUsers([]);
                  findDuplicates(allCompanies);
                  setShowUserFilter(false);
                }}
                className={`px-4 py-2 rounded font-semibold transition ${
                  isDarkMode 
                    ? 'bg-gray-600 hover:bg-gray-500 text-white' 
                    : 'bg-gray-300 hover:bg-gray-400 text-gray-800'
                }`}
              >
                Zrušit filtr
              </button>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowUserFilter(false)}
                  className={`px-4 py-2 rounded font-semibold transition ${
                    isDarkMode 
                      ? 'bg-gray-600 hover:bg-gray-500 text-white' 
                      : 'bg-gray-300 hover:bg-gray-400 text-gray-800'
                  }`}
                >
                  Zrušit
                </button>
                <button
                  onClick={applyUserFilter}
                  className={`px-4 py-2 rounded font-semibold transition ${
                    isDarkMode 
                      ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                      : 'bg-blue-500 hover:bg-blue-600 text-white'
                  }`}
                >
                  Použít filtr ({selectedUsers.length})
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal pro nastavení priorit */}
      {showPriorityModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className={`max-w-3xl w-full max-h-[85vh] overflow-hidden rounded-lg shadow-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`px-6 py-4 border-b ${isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-50'}`}>
              <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                ⚡ Automatické označení podle priorit
              </h3>
              <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Nastavte preferované obchodníky - jejich kontakty budou mít přednost při odstraňování duplicit
              </p>
            </div>

            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {/* Informace o systému priorit */}
              <div className={`mb-6 p-4 rounded-lg ${isDarkMode ? 'bg-blue-900 border border-blue-700' : 'bg-blue-50 border border-blue-200'}`}>
                <h4 className={`font-semibold mb-2 ${isDarkMode ? 'text-blue-100' : 'text-blue-900'}`}>
                  📋 Jak funguje prioritizace?
                </h4>
                <ol className={`text-sm space-y-1 ${isDarkMode ? 'text-blue-200' : 'text-blue-800'}`}>
                  <li><strong>1. Priorita:</strong> Preferovaní obchodníci (níže vybraní) - jejich kontakty se NEOZNAČÍ</li>
                  <li><strong>2. Priorita:</strong> Role obchodníků (admin/teamleader/salesman) - vyšší než kontakty a databáze</li>
                  <li><strong>3. Priorita:</strong> Role kontaktů - vyšší než databáze</li>
                  <li><strong>4. Priorita:</strong> Databáze - nejnižší priorita</li>
                </ol>
                <p className={`mt-3 text-sm font-medium ${isDarkMode ? 'text-yellow-200' : 'text-yellow-800'}`}>
                  ⚠️ Z každé skupiny duplicit zůstane NEOZNAČENÝ kontakt s nejvyšší prioritou, ostatní budou označeny ke smazání.
                </p>
              </div>

              {/* Výběr preferovaných obchodníků */}
              <h4 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Vyberte preferované obchodníky:
              </h4>
              <div className="space-y-2">
                {users
                  .filter(u => u.role === 'admin' || u.role === 'teamleader' || u.role === 'salesman')
                  .map(user => (
                    <label
                      key={user._id}
                      className={`flex items-center gap-3 p-3 rounded cursor-pointer transition ${
                        tempPreferredUsers.includes(user._id)
                          ? (isDarkMode ? 'bg-purple-900 border-purple-600' : 'bg-purple-50 border-purple-300')
                          : (isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-50 hover:bg-gray-100')
                      } border`}
                    >
                      <input
                        type="checkbox"
                        checked={tempPreferredUsers.includes(user._id)}
                        onChange={() => {
                          setTempPreferredUsers(prev => 
                            prev.includes(user._id)
                              ? prev.filter(id => id !== user._id)
                              : [...prev, user._id]
                          );
                        }}
                        className="w-5 h-5"
                      />
                      <div className="flex-1">
                        <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {user.username || user.name || 'Bez jména'}
                        </div>
                        {user.email && (
                          <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            {user.email} • Role: {user.role}
                          </div>
                        )}
                      </div>
                    </label>
                  ))}
              </div>

              {tempPreferredUsers.length === 0 && (
                <p className={`mt-4 text-sm italic ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Pokud nevyberete žádné preferované obchodníky, použije se pouze prioritizace podle rolí.
                </p>
              )}
            </div>

            <div className={`px-6 py-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} flex justify-between`}>
              <button
                onClick={() => {
                  setTempPreferredUsers([]);
                  setPreferredUsers([]);
                }}
                className={`px-4 py-2 rounded font-semibold transition ${
                  isDarkMode 
                    ? 'bg-gray-600 hover:bg-gray-500 text-white' 
                    : 'bg-gray-300 hover:bg-gray-400 text-gray-800'
                }`}
              >
                Vymazat výběr
              </button>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setTempPreferredUsers(preferredUsers);
                    setShowPriorityModal(false);
                  }}
                  className={`px-4 py-2 rounded font-semibold transition ${
                    isDarkMode 
                      ? 'bg-gray-600 hover:bg-gray-500 text-white' 
                      : 'bg-gray-300 hover:bg-gray-400 text-gray-800'
                  }`}
                >
                  Zrušit
                </button>
                <button
                  onClick={() => {
                    setPreferredUsers(tempPreferredUsers);
                    setShowPriorityModal(false);
                    // Automaticky spustit označení
                    setTimeout(() => autoMarkByPriority(), 100);
                  }}
                  className={`px-4 py-2 rounded font-semibold transition ${
                    isDarkMode 
                      ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                      : 'bg-purple-500 hover:bg-purple-600 text-white'
                  }`}
                >
                  ⚡ Označit duplicity ({tempPreferredUsers.length} preferovaných)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}